import React from 'react';
import { Alert, Box, Button, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import FutureIngestionNotice from './FutureIngestionNotice';

const activePhases = ['trial_queued', 'trial_running', 'queued', 'running', 'aggregating'];

export function ProjectChatJobGuidance({ guidance, connected = false, locked = false, recheckStatus = 'idle',
  onRecheck, onPrepareRemaining, onPrepareRetry, onOpenEvent }) {
  const { t } = useTranslation();
  return <Stack spacing={1.5}>
    <Typography variant="body2">{t(guidance.target === 'analysis' ? 'project_chat.analysis.inputs' : guidance.target === 'widget' ? 'project_chat.job.recovery.widget_inputs' : 'project_chat.job.recovery.saved', {
      saved: guidance.saved, remaining: guidance.remaining, count: guidance.inputs_prepared, total: guidance.total
    })}</Typography>
    {guidance.items?.map(item => <Box key={item.event_id}>
      <Typography variant="body2">{t(`project_chat.job.recovery.event_${item.state}`, { id: item.event_id })}</Typography>
      {onOpenEvent && <Button size="small" disabled={!connected} onClick={() => onOpenEvent(item.event_id)}>
        {t('project_chat.job.recovery.open_event', { id: item.event_id })}
      </Button>}
    </Box>)}
    {guidance.reason && <Typography variant="body2">{t(guidance.reason === 'retry_trial'
      ? 'project_chat.readable.retry_reason' : `project_chat.job.recovery.reasons.${guidance.reason}`)}</Typography>}
    {guidance.reason !== 'publication_review' && <Typography variant="body2">{t(guidance.requires_new_plan ? 'project_chat.next_steps.refresh_plan' :
      guidance.can_prepare_retry ? 'project_chat.readable.retry_trial' :
      guidance.can_prepare_remaining ? 'project_chat.job.recovery.ready' : 'project_chat.job.recovery.check')}</Typography>}
    {recheckStatus !== 'idle' && <Alert role="status" aria-live="polite" severity={recheckStatus === 'error' ? 'warning' : 'info'}>
      {t(`project_chat.job.recovery.recheck_${recheckStatus}`)}
    </Alert>}
    <Stack direction="row" spacing={1} flexWrap="wrap">
      {onRecheck && <Button disabled={!connected || recheckStatus === 'checking'} onClick={onRecheck}>
        {t(recheckStatus === 'checking' ? 'project_chat.job.recovery.recheck_pending' : 'project_chat.job.recovery.recheck')}
      </Button>}
      {guidance.can_prepare_remaining && onPrepareRemaining &&
        <Button variant="contained" disabled={locked} onClick={onPrepareRemaining}>
          {t('project_chat.job.recovery.prepare')}
        </Button>}
      {guidance.can_prepare_retry && onPrepareRetry && <Button variant="contained" disabled={locked} onClick={onPrepareRetry}>
        {t('project_chat.job.recovery.retry')}
      </Button>}
    </Stack>
  </Stack>;
}

export function ProjectChatJobStatus({ status }) {
  const { t } = useTranslation();
  return <Alert severity={status.result_disputed || ['needs_review', 'blocked', 'cancelled'].includes(status.phase) ? 'warning' : 'info'}>
    <Stack spacing={1}>
      <Typography variant="body2">{t(status.result_disputed ? 'project_chat.job.result_disputed' : status.decision?.status_key || `project_chat.job.phases.${status.phase}`)}</Typography>
      <Typography variant="body2">{t('project_chat.job.sample_progress', { count: status.sample_ready, total: status.sample_total })}</Typography>
      <Typography variant="body2">{t(status.target === 'analysis' ? 'project_chat.analysis.inputs' : status.target === 'widget' ? 'project_chat.job.progress' : 'project_chat.job.published', {
        count: status.saved, total: status.total
      })}</Typography>
      {status.code && <Typography variant="body2">{t(`project_chat.codes.${status.code}`, t('project_chat.codes.needs_review'))}</Typography>}
      {status.review_items?.map(item => <Typography variant="body2" key={item.event_id}>
        {t('project_chat.event', { id: item.event_id })}: {t(item.uncertain ? 'project_chat.uncertain' : 'project_chat.job.item_review')}
      </Typography>)}
      {['needs_review', 'cancelled'].includes(status.phase) && !status.sample_ready &&
        <Typography variant="body2">{t('project_chat.job.no_sample')}</Typography>}
    </Stack>
  </Alert>;
}

export default function ProjectChatJob({ job, locked, connected, hasFeedback, canConfirm = true, onConfirm, onCancel, onAdjust,
  onOpenWidget, onRecheck, onPrepareRemaining, onPrepareRetry, onRequestRevision, onInvestigate, recheckStatus, onOpenEvent, renderValues }) {
  const { t } = useTranslation();
  if (!job || job.phase === 'draft' || (job.phase === 'completed' && !job.result_disputed)) return null;
  const active = activePhases.includes(job.phase);
  const trial = ['trial_queued', 'trial_running', 'awaiting_sample_confirmation'].includes(job.phase);
  const sampleReview = job.phase === 'awaiting_sample_confirmation';
  const finalReview = job.phase === 'awaiting_final_review';
  const counts = job.counts || {};
  const prepared = (counts.staged || 0) + (counts.saved || 0);
  const results = (job.samples || []).flatMap(sample => sample.result ? [sample.result] : []);
  const decision = job.decision;
  return <Paper variant="outlined" sx={{ p: 2 }}>
    <Stack spacing={1.5}>
      <Typography variant="subtitle1">{t(job.result_disputed ? 'project_chat.job.result_disputed' : decision?.status_key || `project_chat.job.phases.${job.phase}`)}</Typography>
      {job.previous_saved > 0 && <Typography variant="body2">
        {t('project_chat.job.recovery.saved', { saved: job.previous_saved + (counts.saved || 0),
          remaining: Math.max(0, job.total - (counts.saved || 0)) })}
      </Typography>}
      {job.result_disputed && <Alert severity="warning">{t('project_chat.codes.result_disputed')}</Alert>}
      {job.result_disputed && <Stack direction="row" spacing={1}>
        {onInvestigate && <Button variant="contained" disabled={locked} onClick={onInvestigate}>{t('project_chat.readable.investigate')}</Button>}
        <Button disabled={locked} onClick={onAdjust}>{t('project_chat.adjust')}</Button>
      </Stack>}
      {job.result_scope && !(active && job.result_scope === 'original_sources') && <Alert severity={job.result_scope === 'saved_outputs' ? 'warning' : 'info'}>
        {t(`project_chat.job.scope.${job.result_scope}`)}
      </Alert>}
      {trial && <Alert severity="info">{t('project_chat.job.staged')}</Alert>}
      <FutureIngestionNotice proposal={job.future_ingestion} />
      {trial && job.target === 'widget' && <Typography variant="body2">{t('project_chat.job.widget_trial')}</Typography>}
      {job.target === 'analysis' && <Alert severity="info">{t('project_chat.analysis.private')}</Alert>}
      {active && <Box role="status" aria-live="polite">
        {job.progress_stage === 'repairing' && <Typography variant="body2">{t('project_chat.job.repairing')}</Typography>}
        {job.target === 'analysis' && job.progress_stage && !['extracting', 'repairing'].includes(job.progress_stage) &&
          <Typography variant="body2">{t(`project_chat.outcome.${job.progress_stage}`)}</Typography>}
        {job.visual_progress && job.phase !== 'aggregating' && <Typography variant="body2" sx={{ mb: 1 }}>
          {t(job.visual_progress.kind === 'native_content' ?
            (job.visual_progress.title ? 'project_chat.job.content_progress_named' : 'project_chat.job.content_progress') :
            (job.visual_progress.title ? 'project_chat.job.visual_progress_named' : 'project_chat.job.visual_progress'), {
            source: job.visual_progress.title, id: job.visual_progress.event_id,
            inspected: job.visual_progress.inspected_pages, total: job.visual_progress.total_pages })}
        </Typography>}
        {job.phase !== 'aggregating' && <Typography variant="body2" sx={{ mb: 1 }}>{t(decision?.progress_key || 'project_chat.job.progress', {
          count: prepared, total: trial ? job.samples.length : job.total
        })}</Typography>}
        <LinearProgress variant={job.phase === 'aggregating' ? 'indeterminate' : 'determinate'}
          value={Math.min(100, 100 * prepared / Math.max(1, trial ? job.samples.length : job.total))} />
      </Box>}
      {!trial && job.target === 'event_tag' && <Typography variant="body2">{t('project_chat.job.published', { count: counts.saved || 0 })}</Typography>}
      {job.code && !(job.code === 'incomplete_widget' && job.widget_failure?.code) &&
        !(job.code === 'processing' && job.recovery?.processing_resolved) &&
        <Alert severity="warning">{t(`project_chat.codes.${job.code}`, t('project_chat.codes.needs_review'))}
          {job.diagnostic && <Box component="details" sx={{ mt: 1 }}>
            <Box component="summary" sx={{ cursor: 'pointer' }}>{t('project_chat.job.diagnostic_details')}</Box>
            <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>{job.diagnostic}</Typography>
          </Box>}
        </Alert>}
      {job.widget_failure?.code && <Alert severity="warning">
        {t(`project_chat.job.failures.${job.widget_failure.code}`)}
        {job.widget_failure.reference && <Typography variant="caption" display="block">
          {t('project_chat.job.failures.reference', { reference: job.widget_failure.reference })}
        </Typography>}
      </Alert>}
      {!job.recovery && job.review_items?.map(item => <Typography variant="body2" key={item.event_id}>
        {t('project_chat.event', { id: item.event_id })}: {t(item.uncertain ? 'project_chat.uncertain' : 'project_chat.job.item_review')}
      </Typography>)}
      {job.recovery && <ProjectChatJobGuidance guidance={job.recovery} connected={connected}
        locked={locked || hasFeedback} recheckStatus={recheckStatus} onRecheck={onRecheck}
        onPrepareRemaining={onPrepareRemaining} onPrepareRetry={onPrepareRetry} onOpenEvent={onOpenEvent} />}
      {job.phase === 'needs_review' && <>
        {!results.length && <Typography variant="body2">{t('project_chat.job.no_sample')}</Typography>}
        <Button disabled={locked} onClick={onAdjust}>{t('project_chat.adjust')}</Button>
        {job.recovery?.can_prepare_revision && onRequestRevision && <Button variant="contained" disabled={locked || hasFeedback}
          onClick={onRequestRevision}>{t('project_chat.job.recovery.revise_plan')}</Button>}
      </>}
      {job.widget_id && !active && <Button onClick={onOpenWidget}>{t('project_chat.open_widget')}</Button>}
      {(sampleReview || finalReview) && renderValues(results)}
      {sampleReview && <>
        <Typography>{t(decision?.review_key || (job.target === 'analysis' ? 'project_chat.outcome.private_remaining' : 'project_chat.job.review'), {
          count: job.total, total: job.total, prepared: decision?.prepared || results.length,
          remaining: decision?.remaining ?? Math.max(0, job.total - results.length) })}</Typography>
        <Button variant="contained" disabled={locked || hasFeedback || !canConfirm} onClick={onConfirm}>{t(decision?.continue_key || (job.target === 'analysis' ? 'project_chat.outcome.private_finish_action' : 'project_chat.job.continue'))}</Button>
        <Button disabled={locked} onClick={onAdjust}>{t('project_chat.adjust')}</Button>
      </>}
      {finalReview && <>
        <Button variant="contained" disabled={locked || hasFeedback || !canConfirm} onClick={onConfirm}>{t(job.target === 'analysis' ? 'project_chat.outcome.private_finish_action' : 'project_chat.confirm')}</Button>
      </>}
      {(active || sampleReview) && <Button disabled={!connected || Boolean(job.cancel_requested)} onClick={onCancel}>
        {t(job.cancel_requested ? 'project_chat.job.stopping' : 'project_chat.stop')}
      </Button>}
      {active && <Typography variant="caption" color="text.secondary">{t('project_chat.job.background')}</Typography>}
    </Stack>
  </Paper>;
}
